// Oct 13: new client interface (see helpers/uiBeta.js). Oct 15: on for every client
import { betaPage } from "@/components/BetaPage.js"
import { saveShopifyLink, takeShopifyBounce, clearShopifyLink } from "@/helpers/shopifyLink.js"
import {
    createRouter,
    createWebHistory
} from "vue-router";

import {
    useCurrentUser, useFirebaseAuth
} from "vuefire";

import {
    signOut,
    getAuth,
    onAuthStateChanged
} from "firebase/auth";
import * as Sentry from "@sentry/vue";

import useUserStore from '@/stores/user.js'
import usedbStore from '@/stores/db.js'
import { isReturnWorxHost, pageTitle } from '@/helpers/brand.js'
import { hasPrepWorxPlan, hasReturnWorxClaim, returnWorxVisible } from '@/helpers/returnsAccess.js'
import { applyWorkingAs, clearWorkingAs } from '@/helpers/workingAs.js'

// Detect if we're on the warehouse subdomain
const isWarehouseSubdomain = window.location.hostname.startsWith('warehouse.')

// Nov 2, 2026: on a fresh page load the browser restores the Firebase login a moment
// after the first page starts. Warehouse pages read staff-only data as they open, so
// they wait for that first (wait at most 4 s). Only the first navigation waits.
let authRestored = null
function waitForAuthRestore() {
    if (!authRestored) {
        authRestored = new Promise((resolve) => {
            let done = false
            const finish = () => { if (!done) { done = true; resolve() } }
            try {
                const off = onAuthStateChanged(getAuth(), () => { off(); finish() }, () => finish())
            } catch (e) { finish() }
            setTimeout(finish, 4000)
        })
    }
    return authRestored
}

// Helper to check warehouse session
function getWarehouseSession() {
    try {
        const session = localStorage.getItem('warehouseSession')
        return session ? JSON.parse(session) : null
    } catch {
        return null
    }
}

const routes = [{
        path: "/",
        name: "Dashboard",
        component: betaPage(() => import("@/views/Dashboard/Dashboard.vue"), () => import("@/views/v2/Dashboard.vue")),
        meta: { requires_auth: true, requires_premium: true, title: "Dashboard" }
    },
    {
        path: "/login",
        name: "Login",
        component: () => import("@/views/Auth/Login.vue"),
        meta: { requires_no_auth: true, title: "Login" }
    },
    {
        path: "/forgot",
        name: "Forgot",
        component: () => import("@/views/Auth/Forgot.vue"),
        meta: { requires_no_auth: true, title: "Forgot Password" }
    },
    // {
    //     path: "/register",
    //     name: "Register",
    //     component: () => import("@/views/Auth/Register.vue"),
    //     meta: { requires_no_auth: true, title: "Register" }
    // },
    {
        path: "/logout",
        name: "Logout",
        meta: { logout: true }
    },
    {
        path: "/override",
        name: "Override",
        component: () => import("@/views/Auth/Override.vue"),
        meta: { requires_no_auth: true, title: "Override" }
    },
    {
        path: "/amazon-connect",
        name: "AmazonConnect",
        component: () => import("@/views/Auth/AmazonConnect.vue"),
        meta: { auth_no_preference: true, title: "Connect Amazon Account" }
    },
    {
        path: "/shipments/",
        name: "Shipments",
        component: betaPage(() => import("@/views/Shipments/Shipments.vue"), () => import("@/views/v2/Shipments.vue")),
        meta: { requires_auth: true, requires_premium: true, title: "Shipments" }
    },
    {
        path: "/shipments/inbound/",
        name: "ShipmentsInbound",
        // Oct 22, 2026: new-look clients get the new Shipments page (Inbound tab) here too
        component: betaPage(() => import("@/views/Shipments/Inbound/Inbound.vue"), () => import("@/views/v2/Shipments.vue")),
        meta: { requires_auth: true, requires_premium: true, title: "Inbound Shipments" }
    },
    {
        path: "/shipments/inbound/create",
        name: "ShipmentsInboundCreate",
        component: () => import("@/views/Shipments/Inbound/Create.vue"),
        meta: { requires_auth: true, requires_premium: true, title: "Create Inbound Shipment" }
    },
    {
        path: "/shipments/inbound/:id",
        name: "ShipmentsInboundDetail",
        component: () => import("@/views/Shipments/Inbound/Detail.vue"),
        meta: { requires_auth: true, requires_premium: true, title: "Inbound Shipment" }
    },
    {
        path: "/shipments/outbound/",
        name: "ShipmentsOutbound",
        // Oct 22, 2026: new-look clients get the new Shipments page (Outbound tab) here too
        component: betaPage(() => import("@/views/Shipments/Outbound/Outbound.vue"), () => import("@/views/v2/Shipments.vue")),
        meta: { requires_auth: true, requires_premium: true, title: "Outbound Shipments" }
    },
    {
        path: "/shipments/outbound/create",
        name: "ShipmentsOutboundCreate",
        component: () => import("@/views/Shipments/Outbound/Create.vue"),
        meta: { requires_auth: true, requires_premium: true, title: "Create Outbound Shipment" }
    },
    {
        path: "/shipments/outbound/create-fba",
        name: "ShipmentsOutboundCreateFBA",
        component: () => import("@/views/Shipments/Outbound/CreateFBA.vue"),
        meta: { requires_auth: true, requires_premium: true, title: "Create FBA Shipment" }
    },
    {
        path: "/shipments/fbm",
        name: "ShipmentsFBM",
        component: betaPage(() => import("@/views/Shipments/FBM/FBMOrders.vue"), () => import("@/views/v2/FBMOrders.vue")),
        meta: { requires_auth: true, requires_premium: true, title: "FBM Orders" }
    },
    {
        path: "/shipments/outbound/:id",
        name: "ShipmentsOutboundDetail",
        component: () => import("@/views/Shipments/Outbound/Detail.vue"),
        meta: { requires_auth: true, requires_premium: true, title: "Outbound Shipment" }
    },
    {
        path: "/requests/",
        name: "Requests",
        redirect: "/services/requests", // Oct 28: was a placeholder page (Requests/Requests.vue); name kept for router.push
        meta: { requires_auth: true, requires_premium: true, title: "Requests" }
    },
{
    // Old address of the portal (emails, bookmarks). ReturnWorx now opens as
    // its own app for everyone, PrepWorx clients included.
    path: "/requests/returns",
    name: "RequestsReturns",
    redirect: (to) => ({ name: "ReturnWorxHome", query: to.query })
},
    // ─── ReturnWorx ───
    // The same portal under ReturnWorx URLs, for the ReturnWorx domain and for
    // ReturnWorx-only clients. /start is the public sign-up.
    {
        path: "/start",
        name: "ReturnWorxStart",
        component: () => import("@/views/ReturnWorx/Start.vue"),
        meta: { requires_no_auth: true, returnworx_public: true, title: "Create your account" }
    },
    // The portal: one component, five pages (meta.rwSection picks the slice).
    {
        path: "/returns",
        name: "ReturnWorxHome",
        component: () => import("@/views/Requests/Returns.vue"),
        meta: { requires_auth: true, returnworx_ok: true, rwSection: "home", title: "Home" }
    },
    {
        path: "/returns/decide",
        name: "ReturnWorxDecide",
        component: () => import("@/views/Requests/Returns.vue"),
        meta: { requires_auth: true, returnworx_ok: true, rwSection: "decide", title: "Decide" }
    },
    {
        path: "/returns/inventory",
        name: "ReturnWorxInventory",
        component: () => import("@/views/Requests/Returns.vue"),
        meta: { requires_auth: true, returnworx_ok: true, rwSection: "inventory", title: "Inventory" }
    },
    {
        path: "/returns/billing",
        name: "ReturnWorxBilling",
        component: () => import("@/views/Requests/Returns.vue"),
        meta: { requires_auth: true, returnworx_ok: true, rwSection: "billing", title: "Billing" }
    },
    {
        path: "/returns/messages",
        name: "ReturnWorxMessages",
        component: () => import("@/views/Requests/Returns.vue"),
        meta: { requires_auth: true, returnworx_ok: true, rwSection: "messages", title: "Messages" }
    },
    {
        path: "/returns/setup",
        name: "ReturnWorxSetup",
        component: () => import("@/views/Requests/Returns.vue"),
        meta: { requires_auth: true, returnworx_ok: true, rwSection: "setup", title: "Setup" }
    },
    {
        path: "/account",
        name: "ReturnWorxAccount",
        component: () => import("@/views/ReturnWorx/Account.vue"),
        meta: { requires_auth: true, returnworx_ok: true, title: "Account" }
    },
                
    {
        path: "/requests/inspection",
        name: "RequestsInspection",
        redirect: "/services/requests", // Oct 28: was a placeholder page (Requests/Inspection.vue); name kept for router.push
        meta: { requires_auth: true, requires_premium: true, title: "Inspection Request" }
    },
    {
        path: "/requests/return",
        name: "RequestsReturn",
        redirect: "/services/requests", // Oct 28: was an unfinished test form (Requests/Return.vue); name kept for router.push
        meta: { requires_auth: true, requires_premium: true, title: "Return Request" }
    },
    {
        path: "/requests/forward",
        name: "RequestsForward",
        redirect: "/services/requests", // Oct 28: was a placeholder page (Requests/Forward.vue); name kept for router.push
        meta: { requires_auth: true, requires_premium: true, title: "Forward Request" }
    },
    {
        path: "/requests/disposal",
        name: "RequestsDisposal",
        redirect: "/services/requests", // Oct 28: was a placeholder page (Requests/Disposal.vue); name kept for router.push
        meta: { requires_auth: true, requires_premium: true, title: "Disposal Request" }
    },
    {
        path: "/requests/:id",
        name: "RequestsDetail",
        redirect: "/services/requests", // Oct 28: was a placeholder page (Requests/Detail.vue); name kept for router.push
        meta: { requires_auth: true, requires_premium: true, title: "Request" }
    },
    {
        path: "/inventory",
        name: "Inventory",
        component: betaPage(() => import("@/views/Inventory/Inventory.vue"), () => import("@/views/v2/Inventory.vue")),
        meta: { requires_auth: true, requires_premium: true, title: "Inventory" }
    },
    {
        path: "/inventory/create",
        name: "InventoryCreate",
        redirect: "/inventory", // Oct 28: was a placeholder page (Inventory/Create.vue); name kept for router.push
        meta: { requires_auth: true, requires_premium: true, title: "Create Inventory Item" }
    },
    {
        path: "/inventory/:id",
        name: "InventoryDetail",
        redirect: "/inventory", // Oct 28: was a raw record dump (Inventory/Detail.vue); name kept for router.push
        meta: { requires_auth: true, requires_premium: true, title: "Inventory Item" }
    },
    {
        path: "/billing/",
        name: "Billing",
        component: betaPage(() => import("@/views/Billing/Billing.vue"), () => import("@/views/v2/Billing.vue")),
        meta: { requires_auth: true, requires_premium: true, title: "Billing" }
    },
    {
        path: "/billing/invoices/",
        name: "BillingInvoices",
        redirect: "/billing", // Oct 28: was a placeholder page (Billing/Invoices/Invoices.vue); name kept for router.push
        meta: { requires_auth: true, requires_premium: true, title: "Invoices" }
    },
    {
        path: "/billing/invoices/:id",
        name: "BillingInvoicesDetail",
        redirect: "/billing", // Oct 28: was a placeholder page (Billing/Invoices/Invoice.vue); name kept for router.push
        meta: { requires_auth: true, requires_premium: true, title: "Invoice" }
    },
    {
        path: "/settings/",
        name: "Settings",
        component: betaPage(() => import("@/views/Settings/Settings.vue"), () => import("@/views/v2/Settings/Settings.vue")),
        meta: { requires_auth: true, requires_premium: true, title: "Settings" }
    },
    {
        path: "/settings/profile",
        name: "SettingsProfile",
        redirect: "/settings", // Oct 28: was a placeholder page (Settings/Profile.vue); name kept for router.push
        meta: { requires_auth: true, requires_premium: true, title: "Profile" }
    },
    {
        path: "/settings/notifications",
        name: "SettingsNotifications",
        component: betaPage(() => import("@/views/Settings/Notifications.vue"), () => import("@/views/v2/Settings/Notifications.vue")),
        meta: { requires_auth: true, requires_premium: true, title: "Notifications" }
    },
    {
        path: "/settings/account",
        name: "SettingsAccount",
        redirect: "/settings", // Oct 28: was a placeholder page (Settings/Account.vue); name kept for router.push
        meta: { requires_auth: true, requires_premium: true, title: "Account" }
    },
    {
        path: "/settings/feature-requests",
        name: "SettingsFeatureRequests",
        component: betaPage(() => import("@/views/Settings/FeatureRequests.vue"), () => import("@/views/v2/Settings/FeatureRequests.vue")),
        meta: { requires_auth: true, requires_premium: true, title: "Feature Requests" }
    },
    {
        // Oct 27: Updates from PrepWorx (fee changes, new features, schedule news)
        path: "/updates",
        name: "Updates",
        component: () => import("@/views/Updates.vue"),
        meta: { requires_auth: true, requires_premium: true, title: "Updates" }
    },
    {
        path: "/settings/integrations",
        name: "SettingsIntegrations",
        component: betaPage(() => import("@/views/Settings/Integrations.vue"), () => import("@/views/v2/Settings/Integrations.vue")),
        meta: { requires_auth: true, requires_premium: true, title: "Integrations" }
    },
    {
        // Oct 28: match each Shopify product to a PrepWorx product (used by auto-send)
        path: "/settings/shopify-products",
        name: "SettingsShopifyProducts",
        component: () => import("@/views/Settings/ShopifyProducts.vue"),
        meta: { requires_auth: true, requires_premium: true, title: "Match Shopify products" }
    },
    // ==========================================
    // SERVICES (feature-flagged per client)
    // ==========================================
    {
        path: "/services",
        name: "Services",
        redirect: "/services/orders",
        meta: { requires_auth: true, requires_premium: true }
    },
                   {
        path: "/services/requests",
        name: "ServicesRequests",
        component: betaPage(() => import("@/views/services/ServiceRequests.vue"), () => import("@/views/v2/services/ServiceRequests.vue")),
        meta: { requires_auth: true, requires_premium: true, title: "Service Requests" }
    },
    {
        path: "/services/orders",
        name: "ServicesOrders",
        component: betaPage(() => import("@/views/services/OrderList.vue"), () => import("@/views/v2/services/OrderList.vue")),
        meta: { requires_auth: true, requires_premium: true, title: "Ordering Service" }
    },
    {
        path: "/services/orders/create",
        name: "ServicesOrderCreate",
        component: betaPage(() => import("@/views/services/OrderCreate.vue"), () => import("@/views/v2/services/OrderCreate.vue")),
        meta: { requires_auth: true, requires_premium: true, title: "New Order Request" }
    },
    {
        path: "/services/orders/:id",
        name: "ServicesOrderDetail",
        component: betaPage(() => import("@/views/services/OrderDetail.vue"), () => import("@/views/v2/services/OrderDetail.vue")),
        meta: { requires_auth: true, requires_premium: true, title: "Order Detail" }
    },
    {
        // Oct 30, 2026: the old signup (steps 1-5) is retired. Every old link goes to the
        // current signup at /onboarding/v2. The route names stay so nothing that
        // refers to them breaks.
        path: "/onboarding",
        name: "OnboardingDefault",
        redirect: "/onboarding/v2"
    },
    {
        path: "/onboarding/step1",
        name: "OnboardingAccount",
        redirect: "/onboarding/v2"
    },
    {
        path: "/onboarding/step2",
        name: "OnboardingDiscord",
        redirect: "/onboarding/v2"
    },
    {
        path: "/onboarding/step3",
        name: "OnboardingSurvey",
        redirect: "/onboarding/v2"
    },
    {
        path: "/onboarding/step4",
        name: "OnboardingPayment",
        redirect: "/onboarding/v2"
    },
    {
        path: "/onboarding/step5",
        name: "OnboardingSuccess",
        redirect: "/onboarding/v2"
    },
    {
        path: "/error/400",
        name: "400",
        component: () => import("@/views/Error/400.vue"),
        meta: { auth_no_preference: true }
    },
    {
        path: "/error/500",
        name: "500",
        component: () => import("@/views/Error/500.vue"),
        meta: { auth_no_preference: true }
    },
    {
        path: "/error/503",
        name: "503",
        component: () => import("@/views/Error/503.vue"),
        meta: { auth_no_preference: true }
    },

                // ─── V2 Onboarding ───
{
    path: '/onboarding/v2',
    name: 'OnboardingV2Default',
    meta: { redirect: 'OnboardingV2Account' }
},
{
    path: '/onboarding/v2/account',
    name: 'OnboardingV2Account',
    component: () => import('@/views/onboarding-v2/AccountV2.vue'),
    meta: { requires_no_auth: true, title: 'Create Account' }
},
{
    path: '/onboarding/v2/survey',
    name: 'OnboardingV2Survey',
    component: () => import('@/views/onboarding-v2/SurveyV2.vue'),
    meta: { requires_auth: true, redirect_if_no_auth: 'OnboardingV2Account', title: 'Quick Survey' }
},
{
    path: '/onboarding/v2/payment',
    name: 'OnboardingV2Payment',
    component: () => import('@/views/onboarding-v2/PaymentV2.vue'),
    meta: { requires_auth: true, redirect_if_no_auth: 'OnboardingV2Account', title: 'Payment Setup' }
},
{
    path: '/onboarding/v2/discord',
    name: 'OnboardingV2Discord',
    component: () => import('@/views/onboarding-v2/DiscordV2.vue'),
    meta: { requires_auth: true, redirect_if_no_auth: 'OnboardingV2Account', title: 'Connect Discord' }
},
                
                {
    path: '/onboarding/v2/welcome',
    name: 'OnboardingV2Welcome',
    component: () => import('@/views/onboarding-v2/WelcomeV2.vue'),
    meta: { requires_auth: true, redirect_if_no_auth: 'OnboardingV2Account', title: 'Welcome to PrepWorx' }
},
    // ==========================================
    // WAREHOUSE ROUTES (warehouse.prepworx.io)
    // ==========================================
    {
        path: "/warehouse/login",
        name: "WarehouseLogin",
        component: () => import("@/views/warehouse/WarehouseGate.vue"),
        meta: { warehouse_route: true, warehouse_no_session: true, title: "Warehouse Login" }
    },
                {
        path: "/warehouse/service-requests",
        name: "PrepWorxServiceRequests",
        component: () => import("@/views/warehouse/ServiceRequestsAdmin.vue"),
        meta: { warehouse_route: true, requires_warehouse_session: true, title: "Service Requests" }
    },
                {
    path: "/warehouse/returns",
    name: "WarehouseReturns",
    component: () => import("@/views/warehouse/ReturnsModule.vue"),
    meta: { warehouse_route: true, requires_warehouse_session: true, title: "Returns" }
},
                {
    path: "/warehouse/returnworx",
    name: "RwConsoleMessages",
    component: () => import("@/views/warehouse/returnworx/ConsoleMessages.vue"),
    meta: { warehouse_route: true, requires_warehouse_session: true, title: "ReturnWorx \u2014 Messages" }
},
                {
    path: "/warehouse/returnworx/intake",
    name: "RwConsoleIntake",
    component: () => import("@/views/warehouse/returnworx/ConsoleIntake.vue"),
    meta: { warehouse_route: true, requires_warehouse_session: true, rwTab: "intake", title: "ReturnWorx \u2014 Intake" }
},
                {
    path: "/warehouse/returnworx/storage",
    name: "RwStorage",
    component: () => import("@/views/warehouse/returnworx/ConsoleIntake.vue"),
    meta: { warehouse_route: true, requires_warehouse_session: true, rwTab: "storage", title: "ReturnWorx \u2014 Storage queue" }
},
                {
    path: "/warehouse/returnworx/inventory",
    name: "RwInventory",
    component: () => import("@/views/warehouse/returnworx/ConsoleIntake.vue"),
    meta: { warehouse_route: true, requires_warehouse_session: true, rwTab: "inventory", title: "ReturnWorx \u2014 Inventory" }
},
                {
    path: "/warehouse/returnworx/queue",
    name: "RwQueue",
    component: () => import("@/views/warehouse/returnworx/ConsoleIntake.vue"),
    meta: { warehouse_route: true, requires_warehouse_session: true, rwTab: "queue", title: "ReturnWorx \u2014 Queue" }
},
                {
    path: "/warehouse/returnworx/to-fba",
    name: "RwToFba",
    component: () => import("@/views/warehouse/returnworx/ConsoleIntake.vue"),
    meta: { warehouse_route: true, requires_warehouse_session: true, rwTab: "fba", title: "ReturnWorx \u2014 To FBA" }
},
                {
    path: "/warehouse/returnworx/log",
    name: "RwLog",
    component: () => import("@/views/warehouse/returnworx/ConsoleIntake.vue"),
    meta: { warehouse_route: true, requires_warehouse_session: true, rwTab: "log", title: "ReturnWorx \u2014 Log" }
},
                {
    // Orders clients send for units they keep on our shelf (self-fulfillment).
    path: "/warehouse/returnworx/orders",
    name: "RwSfOrders",
    component: () => import("@/views/warehouse/returnworx/ConsoleSelfFulfill.vue"),
    meta: { warehouse_route: true, requires_warehouse_session: true, title: "ReturnWorx \u2014 Client orders" }
},
                {
    path: "/warehouse/returnworx/liquidation",
    name: "RwConsoleLiquidation",
    component: () => import("@/views/warehouse/returnworx/ConsoleLiquidation.vue"),
    meta: { warehouse_route: true, requires_warehouse_session: true, rwTab: "to_list", title: "ReturnWorx \u2014 Liquidation: To list" }
},
                {
    path: "/warehouse/returnworx/liquidation/active",
    name: "RwLiqActive",
    component: () => import("@/views/warehouse/returnworx/ConsoleLiquidation.vue"),
    meta: { warehouse_route: true, requires_warehouse_session: true, rwTab: "active", title: "ReturnWorx \u2014 Liquidation: Active" }
},
                {
    path: "/warehouse/returnworx/liquidation/offers",
    name: "RwLiqOffers",
    component: () => import("@/views/warehouse/returnworx/ConsoleLiquidation.vue"),
    meta: { warehouse_route: true, requires_warehouse_session: true, rwTab: "offers", title: "ReturnWorx \u2014 Liquidation: Offers" }
},
                {
    path: "/warehouse/returnworx/liquidation/to-ship",
    name: "RwLiqToShip",
    component: () => import("@/views/warehouse/returnworx/ConsoleLiquidation.vue"),
    meta: { warehouse_route: true, requires_warehouse_session: true, rwTab: "to_ship", title: "ReturnWorx \u2014 Liquidation: To ship" }
},
                {
    path: "/warehouse/returnworx/liquidation/sold",
    name: "RwLiqSold",
    component: () => import("@/views/warehouse/returnworx/ConsoleLiquidation.vue"),
    meta: { warehouse_route: true, requires_warehouse_session: true, rwTab: "sold", title: "ReturnWorx \u2014 Liquidation: Settle" }
},
                {
    path: "/warehouse/returnworx/liquidation/import",
    name: "RwLiqImport",
    component: () => import("@/views/warehouse/returnworx/ConsoleLiquidation.vue"),
    meta: { warehouse_route: true, requires_warehouse_session: true, rwTab: "import", title: "ReturnWorx \u2014 Liquidation: Import" }
},
                {
    path: "/warehouse/returnworx/ebay",
    name: "RwConsoleEbay",
    component: () => import("@/views/warehouse/returnworx/ConsoleEbay.vue"),
    meta: { warehouse_route: true, requires_warehouse_session: true, requires_admin: true, title: "ReturnWorx \u2014 eBay" }
},
                {
    path: "/warehouse/returnworx/analytics",
    name: "RwConsoleAnalytics",
    component: () => import("@/views/warehouse/returnworx/ConsoleAnalytics.vue"),
    meta: { warehouse_route: true, requires_warehouse_session: true, requires_admin: true, title: "ReturnWorx \u2014 Analytics" }
},
                {
    path: "/warehouse/returnworx/balances",
    name: "RwConsoleBalances",
    component: () => import("@/views/warehouse/returnworx/ConsoleBalances.vue"),
    meta: { warehouse_route: true, requires_warehouse_session: true, title: "ReturnWorx \u2014 Balances" }
},
                {
    path: "/warehouse/returnworx/fees",
    name: "RwConsoleFees",
    component: () => import("@/views/warehouse/returnworx/ConsoleFees.vue"),
    meta: { warehouse_route: true, requires_warehouse_session: true, requires_admin: true, title: "ReturnWorx \u2014 Fee schedules" }
},
                {
    // The floor screens live inside the ReturnWorx console now. These paths stay
    // as redirects so bench bookmarks and older links land in the right place.
    path: "/warehouse/returns-removals",
    name: "WarehouseReturnsRemovals",
    redirect: { name: "RwConsoleIntake" },
    meta: { warehouse_route: true, requires_warehouse_session: true, title: "Returns & Removals" }
},
                {
    path: "/warehouse/liquidation",
    name: "WarehouseLiquidation",
    redirect: { name: "RwConsoleLiquidation" },
    meta: { warehouse_route: true, requires_warehouse_session: true, title: "Liquidation" }
},
    {
        path: "/warehouse/logout",
        name: "WarehouseLogout",
        meta: { warehouse_logout: true }
    },
    {
        path: "/warehouse",
        name: "WarehouseHome",
        component: () => import("@/views/warehouse/WarehouseHome.vue"),
        meta: { warehouse_route: true, requires_warehouse_session: true, title: "Warehouse" }
    },
    {
        path: "/warehouse/buy-label",
        name: "PrepWorxBuyLabel",
        component: () => import("@/views/warehouse/BuyShippingLabel.vue"),
        meta: { warehouse_route: true, requires_warehouse_session: true, title: "Shipping Labels" }
    },
    {
        path: "/warehouse/shipments",
        name: "PrepWorxShipments",
        component: () => import("@/views/warehouse/ShipmentList.vue"),
        meta: { warehouse_route: true, requires_warehouse_session: true, title: "Shipments" }
    },
    {
        path: "/warehouse/fbm",
        name: "PrepWorxFBMQueue",
        component: () => import("@/views/warehouse/FBMQueue.vue"),
        meta: { warehouse_route: true, requires_warehouse_session: true, title: "FBM Orders" }
    },
    {
        path: "/warehouse/fbm/batch/:id",
        name: "PrepWorxFBMBatch",
        component: () => import("@/views/warehouse/FBMBatch.vue"),
        meta: { warehouse_route: true, requires_warehouse_session: true, title: "FBM Batch" }
    },
    {
        path: "/warehouse/fbm/:id",
        name: "PrepWorxFBMStation",
        component: () => import("@/views/warehouse/FBMStation.vue"),
        meta: { warehouse_route: true, requires_warehouse_session: true, title: "FBM Order" }
    },
    {
        path: "/warehouse/pick/:id",
        name: "PrepWorxPicking",
        component: () => import("@/views/warehouse/PickingStation.vue"),
        meta: { warehouse_route: true, requires_warehouse_session: true, title: "Picking" }
    },
    {
        path: "/warehouse/pack/:id",
        name: "PrepWorxPacking",
        component: () => import("@/views/warehouse/PackingStation.vue"),
        meta: { warehouse_route: true, requires_warehouse_session: true, title: "Packing" }
    },
    {
        path: "/warehouse/ship/:id",
        name: "PrepWorxShipping",
        component: () => import("@/views/warehouse/ShipStation.vue"),
        meta: { warehouse_route: true, requires_warehouse_session: true, title: "Shipping" }
    },
    {
        path: "/warehouse/outbound-bill/:id",
        name: "PrepWorxOutboundBilling",
        component: () => import("@/views/warehouse/OutboundBilling.vue"),
        meta: { warehouse_route: true, requires_warehouse_session: true, title: "Outbound Billing" }
    },
    {
        path: "/warehouse/outbound-record/:id",
        name: "PrepWorxOutboundBillingRecord",
        component: () => import("@/views/warehouse/OutboundBillingRecord.vue"),
        meta: { warehouse_route: true, requires_warehouse_session: true, title: "Billing Record" }
    },
                {
        path: "/warehouse/bill-services",
        name: "PrepWorxBillServices",
        component: () => import("@/views/warehouse/BillServices.vue"),
        meta: { warehouse_route: true, requires_warehouse_session: true, title: "Bill Services" }
    },
    {
        path: "/warehouse/storage",
        name: "PrepWorxStorage",
        component: () => import("@/views/warehouse/Storage.vue"),
        meta: { warehouse_route: true, requires_warehouse_session: true, title: "Storage" }
    },
    {
        path: "/warehouse/inventory",
        name: "PrepWorxInventory",
        component: () => import("@/views/warehouse/WarehouseInventory.vue"),
        meta: { warehouse_route: true, requires_warehouse_session: true, title: "Inventory" }
    },
    {
        path: "/warehouse/unbox-label",
        name: "PrepWorxUnboxLabel",
        component: () => import("@/views/warehouse/UnboxLabelStation.vue"),
        meta: { warehouse_route: true, requires_warehouse_session: true, title: "Unbox & Label" }
    },
    {
        path: "/warehouse/picklist-packing",
        name: "PrepWorxPicklistPacking",
        component: () => import("@/views/warehouse/PickListPackingStation.vue"),
        meta: { warehouse_route: true, requires_warehouse_session: true, title: "Pick List Packing" }
    },
    {
        // Oct 2: replaces the Retool "create outbound" app
        path: "/warehouse/manual-outbound",
        name: "WarehouseManualOutbound",
        component: () => import("@/views/warehouse/ManualOutbound.vue"),
        meta: { warehouse_route: true, requires_warehouse_session: true, title: "Manual Outbound" }
    },
    {
        path: "/warehouse/create-fba",
        name: "WarehouseCreateFBA",
        component: () => import("@/views/warehouse/WarehouseCreateFBA.vue"),
        meta: { warehouse_route: true, requires_warehouse_session: true, title: "Create FBA Shipment" }
    },
                
    {
        path: "/warehouse/bulk-inbound",
        name: "BulkInboundScanner",
        component: () => import("@/views/warehouse/BulkInboundScanner.vue"),
        meta: { warehouse_route: true, requires_warehouse_session: true, title: "Bulk Inbound" }
    },
    {
        path: "/warehouse/inbound",
        name: "InboundShipments",
        component: () => import("@/views/warehouse/InboundShipments.vue"),
        meta: { warehouse_route: true, requires_warehouse_session: true, title: "Inbound Shipments" }
    },

                {
        path: "/warehouse/saved-sessions",
        name: "SavedSessions",
        component: () => import("@/views/warehouse/SavedSessions.vue"),
        meta: { warehouse_route: true, requires_warehouse_session: true, title: "Saved Sessions" }
    },
    {
        // Oct 20: make 2" x 1" FNSKU stickers by hand
        path: "/warehouse/fnsku-labels",
        name: "FnskuLabelMaker",
        component: () => import("@/views/warehouse/FnskuLabelMaker.vue"),
        meta: { warehouse_route: true, requires_warehouse_session: true, title: "FNSKU Label Maker" }
    },
    {
        // Oct 19: every live packing / pick list / unbox session in one place
        path: "/warehouse/live-sessions",
        name: "LiveSessions",
        component: () => import("@/views/warehouse/LiveSessions.vue"),
        meta: { warehouse_route: true, requires_warehouse_session: true, title: "Live Sessions" }
    },
    // ==========================================
    // ADMIN ROUTES (warehouse.prepworx.io/warehouse/admin/*)
    // Requires warehouse session + isAdmin flag
    // ==========================================
    {
        path: "/warehouse/admin/clients",
        name: "AdminClients",
        component: () => import("@/views/warehouse/admin/ClientManagement.vue"),
        meta: { warehouse_route: true, requires_warehouse_session: true, requires_admin: true, title: "Client Management" }
    },
    {
        path: "/warehouse/admin/storage",
        name: "PrepWorxStorageDashboard",
        component: () => import("@/views/warehouse/admin/StorageDashboard.vue"),
        meta: { warehouse_route: true, requires_warehouse_session: true, requires_admin: true, title: "Storage Dashboard" }
    },

    {
        // Oct 27: post Updates clients see in their portal (views/Updates.vue)
        path: "/warehouse/admin/client-updates",
        name: "AdminClientUpdates",
        component: () => import("@/views/warehouse/admin/ClientUpdatesAdmin.vue"),
        meta: { warehouse_route: true, requires_warehouse_session: true, requires_admin: true, title: "Client Updates" }
    },
    {
        path: "/warehouse/admin/notices",
        name: "NoticeBoardAdmin",
        component: () => import("@/views/warehouse/admin/NoticeBoardAdmin.vue"),
        meta: { warehouse_route: true, requires_warehouse_session: true, requires_admin: true, title: "Notice Board" }
    },
                
        {
        path: "/warehouse/ms14/returns",
        name: "MS14Returns",
        component: () => import("@/views/warehouse/MS14Returns.vue"),
        meta: { warehouse_route: true, requires_warehouse_session: true, title: "MS14 Returns" }
    },
    {
        path: "/warehouse/ms14",
        name: "MS14Station",
        component: () => import("@/views/warehouse/MS14Station.vue"),
        meta: { warehouse_route: true, requires_warehouse_session: true, title: "MS14 Receiving" }
    },

    {
        path: "/warehouse/tally",
        name: "TallyBoard",
        component: () => import("@/views/warehouse/TallyBoard.vue"),
        meta: { warehouse_route: true, requires_warehouse_session: true, title: "Tally Board" }
    },

    {
        path: "/warehouse/admin/discord-operator",
        name: "AdminDiscordOperator",
        component: () => import("@/views/warehouse/admin/DiscordOperator.vue"),
        meta: { warehouse_route: true, requires_warehouse_session: true, requires_admin: true, title: "Discord Operator" }
    },

    {
        path: "/warehouse/admin/fbm-materials",
        name: "AdminFbmMaterials",
        component: () => import("@/views/warehouse/admin/FbmMaterials.vue"),
        meta: { warehouse_route: true, requires_warehouse_session: true, requires_admin: true, title: "FBM Materials" }
    },

    {
        path: "/warehouse/admin/feature-requests",
        name: "AdminFeatureRequests",
        component: () => import("@/views/warehouse/admin/FeatureRequestsAdmin.vue"),
        meta: { warehouse_route: true, requires_warehouse_session: true, requires_admin: true, title: "Feature Requests" }
    },

    {
        path: "/warehouse/admin/labels",
        name: "AdminLabelsLog",
        component: () => import("@/views/warehouse/admin/LabelsLog.vue"),
        meta: { warehouse_route: true, requires_warehouse_session: true, requires_admin: true, title: "Labels Log" }
    },

     {
        path: "/warehouse/admin/revenue",
        name: "AdminRevenue",
        component: () => import("@/views/warehouse/admin/RevenueDashboard.vue"),
        meta: { warehouse_route: true, requires_warehouse_session: true, requires_admin: true, title: "Revenue Dashboard" }
    },

    {
        path: "/warehouse/admin/returns",
        name: "AdminReturns",
        redirect: { name: "RwConsoleAnalytics" },
        meta: { warehouse_route: true, requires_warehouse_session: true, requires_admin: true, title: "Returns Analytics" }
    },

    {
        path: "/warehouse/admin/returns-fees",
        name: "AdminReturnsFees",
        redirect: { name: "RwConsoleFees" },
        meta: { warehouse_route: true, requires_warehouse_session: true, requires_admin: true, title: "ReturnWorx Fee Schedules" }
    },

    {
        path: "/warehouse/admin/order-queue",
        name: "AdminOrderQueue",
        component: () => import("@/views/warehouse/admin/AdminOrderQueue.vue"),
        meta: { warehouse_route: true, requires_warehouse_session: true, requires_admin: true, title: "Order Queue" }
    },
    {
        path: "/warehouse/admin/api-usage",
        name: "AdminApiUsage",
        component: () => import("@/views/warehouse/admin/ApiUsageDashboard.vue"),
        meta: { warehouse_route: true, requires_warehouse_session: true, requires_admin: true, title: "API Usage" }
    },
                
    {
        path: "/:pathMatch(.*)*",
        name: "NotFound",
        component: () => import("@/views/Error/404.vue"),
        meta: { auth_no_preference: true }
    }
]

const router = createRouter({
    history: createWebHistory(
        import.meta.env.BASE_URL),
    routes
});

router.beforeEach(async (to, from, next) => {
    const userStore = useUserStore()
    document.title = pageTitle(to.meta.title);

    // ==========================================
    // WAREHOUSE SUBDOMAIN HANDLING
    // ==========================================
    if (isWarehouseSubdomain) {
        await waitForAuthRestore()
        const warehouseSession = getWarehouseSession()
        const isWarehouseRoute = to.meta.warehouse_route || false
        const requiresWarehouseSession = to.meta.requires_warehouse_session || false
        const warehouseNoSession = to.meta.warehouse_no_session || false
        const warehouseLogout = to.meta.warehouse_logout || false
        const requiresAdmin = to.meta.requires_admin || false

        // Handle logout - use localStorage (not sessionStorage)
        if (warehouseLogout) {
            localStorage.removeItem('warehouseSession')
            clearWorkingAs()   // Oct 22: logging out also ends "Working as"
            // Security step 6: workers now also have a Firebase login. End it too.
            try { await signOut(getAuth()) } catch (e) { /* not signed in */ }
            next({ name: 'WarehouseLogin' })
            return
        }

        // Security step 6: sessions from before the new login have no authMode.
        // Send them to log in once more so the device gets a real Firebase login.
        // This only fires when going to the warehouse home screen, never mid-station,
        // so no in-progress work is interrupted.
        // Oct 2: also sessions that fell back to the old browser check
        // (authMode "legacy"); that fallback no longer exists.
        if (warehouseSession && warehouseSession.authMode !== 'firebase' && to.name === 'WarehouseHome') {
            localStorage.removeItem('warehouseSession')
            next({ name: 'WarehouseLogin' })
            return
        }

        // If on warehouse subdomain but trying to access client routes, redirect to warehouse login
        if (!isWarehouseRoute && !to.meta.auth_no_preference) {
            if (warehouseSession) {
                next({ name: 'WarehouseHome' })
            } else {
                next({ name: 'WarehouseLogin' })
            }
            return
        }

        // Warehouse route that requires session but none exists
        if (requiresWarehouseSession && !warehouseSession) {
            next({ name: 'WarehouseLogin' })
            return
        }

        // Admin route but user is not admin — redirect to warehouse home
        if (requiresAdmin && (!warehouseSession || !warehouseSession.isAdmin)) {
            next({ name: 'WarehouseHome' })
            return
        }

        // Already logged in but trying to access login page
        if (warehouseNoSession && warehouseSession) {
            next({ name: 'WarehouseHome' })
            return
        }

        // All good for warehouse routes
        next()
        return
    }

    // ==========================================
    // CLIENT APP (app.prepworx.io) - Original Logic
    // ==========================================
    
    // Block warehouse routes on client subdomain
    if (to.meta.warehouse_route) {
        next({ name: 'Dashboard' })
        return
    }

    const requires_auth = to.meta.requires_auth || false;
    const auth_no_preference = to.meta.auth_no_preference || false;
    const requires_premium = to.meta.requires_premium || false;
    const requires_no_auth = to.meta.requires_no_auth || false;
    const redirect_if_auth = to.meta.redirect_if_auth || "";
    const redirect_if_no_auth = to.meta.redirect_if_no_auth || "";
    const logout = to.meta.logout || false;
    const redirect = to.meta.redirect || false;
    const redirect_url = to.meta.redirect_url || false;

    // ==========================================
    // RETURNWORX
    //
    // Same app, two front doors. On the ReturnWorx domain only the ReturnWorx
    // pages exist. On either domain, a ReturnWorx client without a PrepWorx
    // plan can reach the ReturnWorx pages, and is sent there from anything
    // that would otherwise hit the PrepWorx paywall.
    // ==========================================
    const loggedIn = !!userStore.stateData.isLoggedIn
    const claims = userStore.stateData.claims || {}
    const premium = hasPrepWorxPlan(claims)
    const rwClaim = hasReturnWorxClaim(claims)

    // Oct 28: a client who just installed the PrepWorx Fulfillment app on Shopify
    // arrives with a one-time link code. Keep it through the sign-in, then bring
    // them back to Settings → Integrations once to finish linking the store.
    try {
        if (to.query && to.query.shopify_link) {
            saveShopifyLink(to.query.shopify_link, to.query.shopify_shop, loggedIn && to.name === 'SettingsIntegrations')
        } else if (loggedIn && !logout && !isReturnWorxHost && to.name !== 'SettingsIntegrations' && takeShopifyBounce()) {
            next({ name: 'SettingsIntegrations' })
            return
        }
    } catch (e) { /* never block navigation over this */ }

    // Before launch, ReturnWorx pages don't exist for anyone outside the beta
    // (see RETURNWORX_LAUNCHED). On the ReturnWorx domain there's nowhere else
    // to send them, so they're signed out rather than looped.
    const rwRoute = to.meta.returnworx_ok || to.meta.returnworx_public
    if (!returnWorxVisible(userStore.stateData.uid, claims)) {
        if (isReturnWorxHost && loggedIn && !logout) {
            next({ name: 'Logout' })
            return
        }
        if (rwRoute) {
            next({ name: loggedIn ? 'Dashboard' : 'Login' })
            return
        }
    }

    if (isReturnWorxHost && !logout && !auth_no_preference) {
        if (to.name === 'RequestsReturns') {
            next({ name: 'ReturnWorxHome', query: to.query })
            return
        }
        const rwPage = to.meta.returnworx_ok || to.meta.returnworx_public || ['Login', 'Forgot'].includes(to.name)
        if (!rwPage) {
            next({ name: loggedIn ? 'ReturnWorxHome' : 'Login' })
            return
        }
    }
    if (loggedIn && to.meta.returnworx_ok) {
        // Signed in is enough: the page itself walks anyone not yet set up
        // through rates and card. requires_premium is skipped on purpose.
        next()
        return
    }
    if (requires_premium && loggedIn && !premium && rwClaim) {
        next({ name: 'ReturnWorxHome' })
        return
    }

  if (requires_premium && userStore.stateData.isLoggedIn && (!userStore.stateData.claims.hasOwnProperty("stripeRole") || (userStore.stateData.claims.stripeRole || "") != "premium")) {
    const dbStore = usedbStore()
    const surveyDone = dbStore?.stateData?.user?.information?.onboarding?.survey?.hear && dbStore?.stateData?.user?.information?.onboarding?.survey?.asn
    if (surveyDone) {
        next({ name: "OnboardingV2Payment" })
    } else {
        next({ name: "OnboardingV2Survey" })
    }
    return
}
 
    if (logout) {
        clearShopifyLink()   // Oct 28: a half-finished Shopify link never carries over to the next login
        if (userStore.stateData.isLoggedIn) {
            Sentry.setUser(null);
            OpenReplay.setUserID("")
            userStore.clear() 
            signOut(useFirebaseAuth())
        }
        window.location = router.resolve({
            name: "Login"
        }).href
    }
    else if (requires_auth && !userStore.stateData.isLoggedIn) {
        next({ name: "Login" })
    }
    else if (requires_no_auth && redirect_if_auth != "" && userStore.stateData.isLoggedIn) {
        next({ name: redirect_if_auth })
    }
    else if (requires_auth && redirect_if_no_auth != "" && !userStore.stateData.isLoggedIn) {
        next({ name: redirect_if_no_auth })
    }
    else if (requires_no_auth && userStore.stateData.isLoggedIn) {
        next({ name: "Dashboard" })
    }
    else if (redirect) {
        next({ name: redirect })
    }
    else if (redirect_url) {
        window.location = redirect_url
    }
    else {
        next()
    }
})
// If a page's code fails to load (usually a deploy replaced it while the tab was
// open), reload once. If it fails again right after, show a message instead of
// reloading forever.
router.onError((error, to) => {
    const msg = String((error && error.message) || '')
    const chunkFailed = /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module/i.test(msg)
    if (!chunkFailed) return
    const key = 'pw_chunk_reload'
    let last = null
    try { last = JSON.parse(sessionStorage.getItem(key) || 'null') } catch (e) {}
    if (!(last && last.path === to.fullPath && Date.now() - last.at < 30000)) {
        try { sessionStorage.setItem(key, JSON.stringify({ path: to.fullPath, at: Date.now() })) } catch (e) {}
        window.location = to.fullPath
        return
    }
    alert("This page couldn't load. Close this tab and open the app again. If it keeps happening, send Daniel a screenshot.")
})

// Export helper for components to access warehouse session
// Oct 22: an admin's "Working as" choice swaps in that worker's name/ID here, so
// every station logs under the chosen worker (see helpers/workingAs.js).
export function useWarehouseSession() {
    return applyWorkingAs(getWarehouseSession())
}

export default router;
